import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter } from "react-router-dom";

import { AppLayout } from "./AppLayout";
import { LandingPage } from "@/features/public/LandingPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { FacilityLayout } from "@/features/facilities/FacilityLayout";
import { OverviewPage } from "@/features/facilities/OverviewPage";
import { OwnerOnly } from "@/features/facilities/OwnerOnly";
import { ItemsPage } from "@/features/facilities/ItemsPage";
import { ReportsListPage } from "@/features/reports/ReportsListPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";

/**
 * Anything past the four "hot" routes (Landing, Login, Dashboard,
 * Facility-shell + Overview + Items + Reports list) gets code-split
 * lazily. Biggest wins:
 *   - WalkFacilityPage + ItemMapPage pull in Leaflet (~50 KB gz)
 *   - SettingsPage pulls in qrcode.react
 *   - RegisterPage / PublicReportPage are never visited by signed-in
 *     maintainers in normal flow
 *
 * `lazyDefault` is just `lazy()` with the named-export bridge so each
 * page can keep its named export rather than adding a default per
 * module.
 */
function lazyDefault<T>(loader: () => Promise<T>, name: keyof T) {
  return lazy(() =>
    loader().then((m) => ({ default: m[name] as ComponentType<unknown> })),
  );
}

const RegisterPage = lazyDefault(
  () => import("@/features/auth/RegisterPage"),
  "RegisterPage",
);
const FacilitiesListPage = lazyDefault(
  () => import("@/features/facilities/FacilitiesListPage"),
  "FacilitiesListPage",
);
const MembersPage = lazyDefault(
  () => import("@/features/facilities/MembersPage"),
  "MembersPage",
);
const SettingsPage = lazyDefault(
  () => import("@/features/facilities/SettingsPage"),
  "SettingsPage",
);
const ItemMapPage = lazyDefault(
  () => import("@/features/facilities/ItemMapPage"),
  "ItemMapPage",
);
const WalkFacilityPage = lazyDefault(
  () => import("@/features/facilities/WalkFacilityPage"),
  "WalkFacilityPage",
);
const ReportDetailPage = lazyDefault(
  () => import("@/features/reports/ReportDetailPage"),
  "ReportDetailPage",
);
const TasksListPage = lazyDefault(
  () => import("@/features/tasks/TasksListPage"),
  "TasksListPage",
);
const ProposedTasksPage = lazyDefault(
  () => import("@/features/tasks/ProposedTasksPage"),
  "ProposedTasksPage",
);
const TaskDetailPage = lazyDefault(
  () => import("@/features/tasks/TaskDetailPage"),
  "TaskDetailPage",
);
const PublicReportPage = lazyDefault(
  () => import("@/features/public/PublicReportPage"),
  "PublicReportPage",
);
const PublicReportStatusPage = lazyDefault(
  () => import("@/features/public/PublicReportStatusPage"),
  "PublicReportStatusPage",
);
const PublicStatusPage = lazyDefault(
  () => import("@/features/public/PublicStatusPage"),
  "PublicStatusPage",
);
const JoinPage = lazyDefault(
  () => import("@/features/maintainer/JoinPage"),
  "JoinPage",
);

const RouteFallback = () => (
  <p className="p-6 text-sm text-slate-500">…</p>
);

const lz = (el: React.ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{el}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "register", element: lz(<RegisterPage />) },
      {
        path: "dashboard",
        element: (
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        ),
      },
      {
        path: "facilities",
        element: (
          <RequireAuth>
            {lz(<FacilitiesListPage />)}
          </RequireAuth>
        ),
      },
      {
        path: "facilities/:id",
        element: (
          <RequireAuth>
            <FacilityLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <OverviewPage /> },
          { path: "items", element: <ItemsPage /> },
          { path: "items/map", element: lz(<ItemMapPage />) },
          {
            path: "walk",
            element: <OwnerOnly>{lz(<WalkFacilityPage />)}</OwnerOnly>,
          },
          { path: "reports", element: <ReportsListPage /> },
          { path: "reports/:reportId", element: lz(<ReportDetailPage />) },
          { path: "tasks", element: lz(<TasksListPage />) },
          { path: "tasks/:taskId", element: lz(<TaskDetailPage />) },
          { path: "members", element: lz(<MembersPage />) },
          {
            path: "settings",
            element: <OwnerOnly>{lz(<SettingsPage />)}</OwnerOnly>,
          },
          {
            path: "tasks/proposed",
            element: <OwnerOnly>{lz(<ProposedTasksPage />)}</OwnerOnly>,
          },
        ],
      },
      { path: "join/:token", element: lz(<JoinPage />) },
      { path: "f/:token", element: lz(<PublicStatusPage />) },
      { path: "f/:token/report", element: lz(<PublicReportPage />) },
      { path: "f/:token/r/:reportId", element: lz(<PublicReportStatusPage />) },
    ],
  },
]);
